import React, { useEffect, useState } from "react";
import { registrationService } from "../../firebase/services";
import { useAuth } from "../../context/AuthContext";
import {
  UserCheck,
  UserX,
  Eye,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Search,
  Filter,
  User,
  ExternalLink,
  Shield,
  Mail,
  Phone,
  GraduationCap,
  Sparkles,
} from "lucide-react";

export const AdminRegistrations = () => {
  const { currentUser } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReg, setSelectedReg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState({ type: "", text: "" });

  const fetchRegs = async () => {
    setLoading(true);
    try {
      const data = await registrationService.getRegistrations(statusFilter);
      setRegistrations(data);
    } catch (err) {
      console.error("Fetch registrations error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegs();
  }, [statusFilter]);

  const handleApprove = async (reg) => {
    if (!window.confirm(`Are you sure you want to approve ${reg.fullName}? This will approve the application and assign a unique Student ID. The applicant will activate their own Firebase account using the private activation code from registration.`)) {
      return;
    }

    setActionLoading(true);
    setActionMsg({ type: "", text: "" });
    try {
      const result = await registrationService.approveRegistration(reg.id);
      setActionMsg({
        type: "success",
        text: result.emailSent
          ? `Successfully approved ${reg.fullName}! Assigned Student ID: ${result.studentId}. Activation email sent to ${reg.email}.`
          : `Approved ${reg.fullName} and assigned Student ID ${result.studentId}, but the activation email could not be sent. ${result.emailError || "Check EmailJS configuration."}`,
      });
      if (selectedReg?.id === reg.id) {
        setSelectedReg((prev) => ({ ...prev, status: "approved", studentId: result.studentId }));
      }
      fetchRegs();
    } catch (err) {
      console.error("Approval error:", err);
      setActionMsg({ type: "error", text: err.message || "Failed to approve registration." });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendActivation = async (reg) => {
    setActionLoading(true);
    setActionMsg({ type: "", text: "" });
    try {
      await registrationService.resendActivationEmail(reg.id);
      setActionMsg({ type: "success", text: `Activation email sent to ${reg.email}.` });
    } catch (err) {
      setActionMsg({ type: "error", text: err.message || "Could not send activation email." });
    } finally { setActionLoading(false); }
  };

  const handleReject = async (reg) => {
    const reason = window.prompt("Please enter reason for rejection (optional):", "Does not meet entry criteria / duplicate application");
    if (reason === null) return;

    setActionLoading(true);
    setActionMsg({ type: "", text: "" });
    try {
      await registrationService.rejectRegistration(reg.id, reason);
      setActionMsg({
        type: "success",
        text: `Application for ${reg.fullName} has been marked as rejected.`,
      });
      if (selectedReg?.id === reg.id) {
        setSelectedReg((prev) => ({ ...prev, status: "rejected" }));
      }
      fetchRegs();
    } catch (err) {
      console.error("Rejection error:", err);
      setActionMsg({ type: "error", text: err.message || "Failed to reject registration." });
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRegistrations = registrations.filter((r) => {
    const q = searchQuery.toLowerCase();
    return (
      (r.fullName && r.fullName.toLowerCase().includes(q)) ||
      (r.email && r.email.toLowerCase().includes(q)) ||
      (r.college && r.college.toLowerCase().includes(q)) ||
      (r.applicationNumber && r.applicationNumber.toLowerCase().includes(q)) ||
      (r.studentId && r.studentId.toLowerCase().includes(q))
    );
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Student Registration Applications</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Review, verify passport photos, and approve 15 Days Masterclass applicants.
          </p>
        </div>
      </div>

      {actionMsg.text && (
        <div
          data-testid="admin-action-msg"
          className={`p-4 rounded-lg text-xs font-mono flex items-start gap-2 ${
            actionMsg.type === "success"
              ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
              : "bg-destructive/10 border border-destructive/30 text-destructive"
          }`}
        >
          {actionMsg.type === "success" ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          )}
          <span>{actionMsg.text}</span>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="bg-card border border-border rounded-xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-2.5" />
          <input
            type="text"
            data-testid="admin-search-input"
            placeholder="Search by name, email, college, or ref ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
          />
        </div>

        <div className="flex items-center space-x-2">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <div className="flex rounded-lg border border-border p-0.5 bg-muted/40 text-xs font-mono">
            {["all", "pending", "approved", "rejected"].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                data-testid={`filter-${st}-btn`}
                className={`px-3 py-1 rounded-md capitalize font-medium transition-colors ${
                  statusFilter === st
                    ? "bg-card text-foreground shadow-sm font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table of Registrations */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            <span className="text-xs font-mono">Querying registrations from Firestore...</span>
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground space-y-2">
            <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="text-sm font-semibold text-foreground">No registrations found.</div>
            <p className="text-xs max-w-sm mx-auto font-mono">
              Applications submitted via <code>/register</code> will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-mono">
              <thead className="bg-muted/50 border-b border-border text-muted-foreground uppercase text-[10px]">
                <tr>
                  <th className="px-4 py-3">Applicant Name</th>
                  <th className="px-4 py-3">Contact & Email</th>
                  <th className="px-4 py-3">College & Course</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredRegistrations.map((reg) => (
                  <tr key={reg.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-foreground font-sans text-sm">
                        {reg.fullName}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {reg.studentId ? (
                          <span className="text-emerald-400 font-bold">{reg.studentId}</span>
                        ) : (
                          <span>Ref: {reg.applicationNumber || reg.id.slice(0, 8)}</span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-foreground">{reg.email}</div>
                      <div className="text-muted-foreground text-[11px]">{reg.mobile}</div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-foreground truncate max-w-[200px]">{reg.college}</div>
                      <div className="text-muted-foreground text-[11px]">
                        {reg.course} {reg.branch ? `(${reg.branch})` : ""}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                      {reg.createdAt?.toDate
                        ? reg.createdAt.toDate().toLocaleDateString()
                        : "Recent"}
                    </td>

                    <td className="px-4 py-3">
                      {reg.status === "approved" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
                          <CheckCircle2 className="w-3 h-3" />
                          Approved
                        </span>
                      ) : reg.status === "rejected" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-rose-500/10 text-rose-400 border border-rose-500/20 font-semibold">
                          <XCircle className="w-3 h-3" />
                          Rejected
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/20 font-semibold">
                          Pending
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1.5">
                        <button
                          onClick={() => setSelectedReg(reg)}
                          data-testid={`view-reg-${reg.id}`}
                          className="px-2.5 py-1 rounded bg-muted hover:bg-muted/80 text-foreground border border-border text-[11px] flex items-center gap-1 transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          <span>View</span>
                        </button>

                        {reg.status === "pending" && (
                          <>
                            <button
                              onClick={() => handleApprove(reg)}
                              disabled={actionLoading}
                              data-testid={`approve-reg-${reg.id}`}
                              className="px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-semibold flex items-center gap-1 transition-colors disabled:opacity-50"
                            >
                              <CheckCircle2 className="w-3 h-3" />
                              <span>Approve</span>
                            </button>
                            {reg.status === "approved" && (
                              <button onClick={() => handleResendActivation(reg)} disabled={actionLoading} className="px-2 py-1.5 rounded-md border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 inline-flex items-center gap-1" title="Resend activation email">
                                <Mail className="w-3.5 h-3.5" /> Resend
                              </button>
                            )}
                            <button
                              onClick={() => handleReject(reg)}
                              disabled={actionLoading}
                              data-testid={`reject-reg-${reg.id}`}
                              className="px-2.5 py-1 rounded bg-rose-600/20 hover:bg-rose-600/30 text-rose-400 border border-rose-500/30 text-[11px] flex items-center gap-1 transition-colors disabled:opacity-50"
                            >
                              <XCircle className="w-3 h-3" />
                              <span>Reject</span>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Application Detail View Modal */}
      {selectedReg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-card border border-border max-w-2xl w-full rounded-2xl p-6 sm:p-8 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {selectedReg.status} application
                </span>
                <h2 className="text-xl font-bold mt-1 text-foreground">
                  {selectedReg.fullName}
                </h2>
                <p className="text-xs font-mono text-muted-foreground">
                  Ref: {selectedReg.applicationNumber || selectedReg.id}
                  {selectedReg.studentId && (
                    <span className="ml-2 text-emerald-400 font-bold">
                      • Student ID: {selectedReg.studentId}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => setSelectedReg(null)}
                className="p-1 rounded-lg border border-border text-muted-foreground hover:text-foreground text-xs font-mono"
              >
                ✕ Close
              </button>
            </div>

            {/* Passport Photo and Primary Bio */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 p-4 rounded-xl bg-muted/40 border border-border">
              {selectedReg.photoUrl ? (
                <img
                  src={selectedReg.photoUrl}
                  alt="Applicant Passport"
                  className="w-24 h-30 rounded-lg object-cover border border-border shadow-sm flex-shrink-0"
                />
              ) : (
                <div className="w-24 h-30 rounded-lg bg-muted border border-border flex items-center justify-center text-muted-foreground flex-shrink-0">
                  <User className="w-8 h-8 opacity-40" />
                </div>
              )}
              <div className="space-y-1.5 text-xs font-mono flex-1">
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-foreground">{selectedReg.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-foreground">{selectedReg.mobile}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Location: </span>
                  <span className="text-foreground">
                    {selectedReg.city}, {selectedReg.state || "Chhattisgarh"}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Experience: </span>
                  <span className="text-cyan-400 font-semibold">{selectedReg.experience}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Track: </span>
                  <span className="text-emerald-400 font-semibold">{selectedReg.interestedArea}</span>
                </div>
              </div>
            </div>

            {/* Academic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-mono">
              <div className="p-3 rounded-lg bg-muted/30 border border-border space-y-1">
                <span className="text-muted-foreground text-[10px]">College / Institute</span>
                <div className="font-semibold text-foreground">{selectedReg.college}</div>
              </div>

              <div className="p-3 rounded-lg bg-muted/30 border border-border space-y-1">
                <span className="text-muted-foreground text-[10px]">Course / Degree</span>
                <div className="font-semibold text-foreground">
                  {selectedReg.course} {selectedReg.branch ? `(${selectedReg.branch})` : ""}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-muted/30 border border-border space-y-1">
                <span className="text-muted-foreground text-[10px]">Year / Semester</span>
                <div className="font-semibold text-foreground">{selectedReg.yearSemester}</div>
              </div>

              <div className="p-3 rounded-lg bg-muted/30 border border-border space-y-1">
                <span className="text-muted-foreground text-[10px]">Graduation Year</span>
                <div className="font-semibold text-foreground">{selectedReg.graduationYear || "N/A"}</div>
              </div>
            </div>

            {/* Statement of Purpose */}
            <div className="p-4 rounded-xl bg-muted/30 border border-border space-y-1.5">
              <span className="text-[10px] font-mono text-muted-foreground uppercase">
                Why do you want to join this Masterclass?
              </span>
              <p className="text-xs text-foreground leading-relaxed italic">
                "{selectedReg.statementOfPurpose || "No statement provided."}"
              </p>
            </div>

            {/* Actions in Modal */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <button
                onClick={() => setSelectedReg(null)}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted text-xs font-mono"
              >
                Close
              </button>
              {selectedReg.status === "pending" && (
                <>
                  <button
                    onClick={() => handleReject(selectedReg)}
                    disabled={actionLoading}
                    className="px-4 py-2 rounded-lg bg-rose-600/20 hover:bg-rose-600/30 text-rose-400 border border-rose-500/30 text-xs font-mono font-semibold"
                  >
                    Reject Application
                  </button>
                  <button
                    onClick={() => handleApprove(selectedReg)}
                    disabled={actionLoading}
                    className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-mono font-semibold shadow-md flex items-center gap-1.5"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Approve & Assign Student ID</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
